import { FilterChip } from "@freightsimple/ui-core/Display/FilterChip";
import type { ReactNode } from "react";
import { displayNameForCarrier } from "@/Data/displayNameForCarrier";
import { ShipmentState } from "@freightsimple/generated-dashboard-openapi-client/models/ShipmentState";
import { formatPostalCode, startsWithNumber } from "@/Helpers/formatPostalCode";
import {
  fullStateOrProvinceName,
  isCanadianProvince,
} from "@/Helpers/fullStateOrProvinceName";
import moment from "moment";
import type { FilterShipmentsDescription } from "@/Shared/ShipmentFilter/Dialogs/Internal/FilterShipmentsDialogBody";

interface FilterPillsProps {
  filter: FilterShipmentsDescription;
  setFilter: (newfilter: FilterShipmentsDescription) => void;
}

function descriptionForState(state: ShipmentState) {
  if (state === ShipmentState.BookingConfirmed) {
    return "Booked";
  }

  if (state === ShipmentState.InTransit) {
    return "In Transit";
  }

  if (state === ShipmentState.Delivered) {
    return "Delivered";
  }

  if (state === ShipmentState.Cancelled) {
    return "Cancelled";
  }

  if (state === ShipmentState.Lost) {
    return "Lost";
  }

  return "Other";
}

function formatDate(value: string) {
  return moment(value).format("Do MMM YYYY");
}

/**
 * A postal code's label depends on where it is: one starting with a digit is a
 * US zipcode, anything else a Canadian postal code. Same for the region — BC is
 * a province, CA a state. Small, but jarring when wrong, so both branches are
 * pinned by the EveryPill story.
 */
function postalCodeLabel(side: "Pickup" | "Delivery") {
  return (value: string) =>
    startsWithNumber(value) ? `${side} Zipcode` : `${side} Postal Code`;
}

function regionLabel(side: "Pickup" | "Delivery") {
  return (value: string) =>
    isCanadianProvince(value) ? `${side} Province` : `${side} State`;
}

export function ViewShipmentsFilterPills(props: FilterPillsProps) {
  const { filter, setFilter } = props;
  const pills: ReactNode[] = [];

  /**
   * Adds one pill, when that field is set. `label` and `describe` may each be a
   * function of the value, which is what lets the postal-code and region pills
   * rename themselves and the prices and dates format themselves. The field's
   * own type flows through `K`, so each callback receives the right type.
   */
  function add<K extends keyof FilterShipmentsDescription>(
    key: string,
    field: K,
    label:
      | string
      | ((value: NonNullable<FilterShipmentsDescription[K]>) => string),
    describe?: (value: NonNullable<FilterShipmentsDescription[K]>) => string,
  ) {
    const value = filter[field];
    if (value === undefined) {
      return;
    }

    pills.push(
      <FilterChip
        key={key}
        label={typeof label === "function" ? label(value) : label}
        value={describe === undefined ? String(value) : describe(value)}
        onDismiss={function () {
          setFilter({ ...filter, [field]: undefined });
        }}
      />,
    );
  }

  // The order here is the order they appear on screen, and the snapshot pins it.
  add("pro-number", "proNumber", "PRO Number");
  add("bol-number", "bolNumber", "BOL Number");
  add("carrier", "carrierIdentifier", "Carrier", displayNameForCarrier);
  add("min-price", "minPriceDollars", "Min Price", (value) => `$${value}`);
  add("max-price", "maxPriceDollars", "Max Price", (value) => `$${value}`);
  add("shipment-state", "shipmentState", "States", descriptionForState);
  add("pickup-reference", "pickupReferenceNumber", "Pickup Reference Number");
  add("pickup-business", "pickupBusinessName", "Pickup Business Name");
  add(
    "pickup-postal",
    "pickupPostalCode",
    postalCodeLabel("Pickup"),
    formatPostalCode,
  );
  add(
    "pickup-state-province",
    "pickupStateOrProvinceCode",
    regionLabel("Pickup"),
    fullStateOrProvinceName,
  );
  add(
    "delivery-reference",
    "deliveryReferenceNumber",
    "Delivery Reference Number",
  );
  add("delivery-business", "deliveryBusinessName", "Delivery Business Name");
  add(
    "delivery-postal",
    "deliveryPostalCode",
    postalCodeLabel("Delivery"),
    formatPostalCode,
  );
  add(
    "delivery-state-province",
    "deliveryStateOrProvinceCode",
    regionLabel("Delivery"),
    fullStateOrProvinceName,
  );
  add("booked-start", "bookedAtStartRange", "Booked From", formatDate);
  add("booked-end", "bookedAtEndRange", "Booked Until", formatDate);
  add(
    "pickup-date-start",
    "pickupDateStartRange",
    "Picked up from",
    formatDate,
  );
  add("pickup-date-end", "pickupDateEndRange", "Picked up until", formatDate);
  add("delivered-start", "deliveredAtStartRange", "Delivered from", formatDate);
  add("delivered-end", "deliveredAtEndRange", "Delivered until", formatDate);
  // The only valueless pill — the toggle has nothing to show beyond its label.
  add(
    "freight-claims",
    "onlyShowFreightClaims",
    "Only Shipments with Freight Claims",
    () => "",
  );

  return (
    // Flex with a gap, and wrapping: the pills used to sit in a plain block with
    // nothing between them, so they rendered edge to edge and read as one long
    // control. They also have to wrap — a shipment can carry enough filters to
    // outrun the bar.
    <div
      className="flex flex-wrap items-center gap-2"
      style={{ paddingLeft: "8px", marginLeft: "8px", marginTop: "4px" }}
    >
      {pills}
    </div>
  );
}
